import { loadCaptainUpScript } from "./CaptainUpLoader";
import type { CaptainUpTheme, CaptainUpWidgetApi } from "./CaptainUpTypes";
import { debugLog, debugWarn } from "@/utils/debug";
import type { CaptainUpSecureAuth } from "@/integrations/captainup/CaptainUpAuthService";

type DemoPlayer = { playerId: string; playerName: string };

export type CaptainUpBootContext = {
	gameName?: string;
	lang?: string;
	modulesCsv?: string;
	theme?: CaptainUpTheme;
};

export type CaptainUpAuthMode =
	| { mode: "demo" }
	| { mode: "secure"; auth: CaptainUpSecureAuth };

export type CaptainUpDomIds = {
	missions: "_cpt_popup9_ruby_missions";
	tournament: "_cpt_popup9_tournament";
	modal: "_cpt_popup9_modal";
};

type ElementDebugSnapshot = {
	id: string;
	parentId: string | null;
	childCount: number;
	innerHtmlLength: number;
	isConnected: boolean;
};

export type CaptainUpModalContentHandler = (hasContent: boolean) => void;

export type CaptainUpDockContentState = {
	missionHasContent: boolean;
	tournamentHasContent: boolean;
};

export type CaptainUpDockContentHandler = (
	contentState: CaptainUpDockContentState
) => void;

function snapshot(el: HTMLElement): ElementDebugSnapshot {
	return {
		id: el.id || "(no-id)",
		parentId: el.parentElement?.id || null,
		childCount: el.childElementCount,
		innerHtmlLength: (el.innerHTML || "").length,
		isConnected: el.isConnected,
	};
}

function describeWidgetApi(api: CaptainUpWidgetApi | undefined): Record<string, boolean> {
	return {
		setTheme: typeof api?.setTheme === "function",
		enableModules: typeof api?.enableModules === "function",
		setSignedUser: typeof api?.setSignedUser === "function",
		setBrand: typeof api?.setBrand === "function",
		setAuthToken: typeof api?.setAuthToken === "function",
		login: typeof api?.login === "function",
		renderer: typeof api?.renderer === "function",
		setGameSectionsConfig: typeof api?.setGameSectionsConfig === "function",
		ui: typeof api?.ui === "function",
		onUserInteraction: typeof api?.onUserInteraction === "function",
	};
}

function summarizeAuthMode(authMode: CaptainUpAuthMode): Record<string, unknown> {
	if (authMode.mode === "demo") {
		return {
			mode: "demo",
		};
	}

	return {
		mode: "secure",
		hasSignedUser: String(authMode.auth.signedUser ?? "").trim().length > 0,
		hasBrandId:
			typeof authMode.auth.brandId === "number" && Number.isFinite(authMode.auth.brandId),
		hasCluster: String(authMode.auth.cluster ?? "").trim().length > 0,
		userIdPresent:
			typeof authMode.auth.user?.id !== "undefined" &&
			authMode.auth.user?.id !== null &&
			String(authMode.auth.user.id).trim().length > 0,
		userNamePresent:
			typeof authMode.auth.user?.name === "string" &&
			authMode.auth.user.name.trim().length > 0,
		userImagePresent:
			typeof authMode.auth.user?.image === "string" &&
			authMode.auth.user.image.trim().length > 0,
	};
}

function elementHasMeaningfulContent(el: HTMLElement): boolean {
	if (el.childElementCount > 0) {
		return true;
	}

	return (el.textContent ?? "").trim().length > 0;
}

function areDockContentStatesEqual(
	a: CaptainUpDockContentState,
	b: CaptainUpDockContentState
): boolean {
	return (
		a.missionHasContent === b.missionHasContent &&
		a.tournamentHasContent === b.tournamentHasContent
	);
}

export class CaptainUpDomController {
	public static readonly IDS: CaptainUpDomIds = {
		missions: "_cpt_popup9_ruby_missions",
		tournament: "_cpt_popup9_tournament",
		modal: "_cpt_popup9_modal",
	};

	private missionsEl: HTMLElement;
	private tournamentEl: HTMLElement;
	private modalEl: HTMLElement;

	private modalGuardian: MutationObserver | null = null;
	private modalContentObserver: MutationObserver | null = null;
	private dockContentObserver: MutationObserver | null = null;

	private isReinsertingModal = false;
	private currentModalHost: HTMLElement | null = null;

	private modalContentHandlers = new Set<CaptainUpModalContentHandler>();
	private dockContentHandlers = new Set<CaptainUpDockContentHandler>();

	private lastKnownModalHasContent: boolean;
	private lastKnownDockContentState: CaptainUpDockContentState;

	public constructor() {
		this.missionsEl = this.ensureElement(CaptainUpDomController.IDS.missions);
		this.tournamentEl = this.ensureElement(CaptainUpDomController.IDS.tournament);
		this.modalEl = this.ensureElement(CaptainUpDomController.IDS.modal);

		this.lastKnownModalHasContent = elementHasMeaningfulContent(this.modalEl);
		this.lastKnownDockContentState = this.readDockContentState();

		debugLog("[CaptainUpDOM] controller constructed", {
			mission: snapshot(this.missionsEl),
			tournament: snapshot(this.tournamentEl),
			modal: snapshot(this.modalEl),
			initialModalHasContent: this.lastKnownModalHasContent,
			initialDockContentState: this.lastKnownDockContentState,
		});
	}

	public attachMission(host: HTMLElement): void {
		this.attachElement(this.missionsEl, host);
	}

	public parkMission(parkingHost: HTMLElement): void {
		this.attachElement(this.missionsEl, parkingHost);
	}

	public attachTournament(host: HTMLElement): void {
		this.attachElement(this.tournamentEl, host);
	}

	public parkTournament(parkingHost: HTMLElement): void {
		this.attachElement(this.tournamentEl, parkingHost);
	}

	public attachModal(modalHost: HTMLElement): void {
		this.currentModalHost = modalHost;
		this.attachElement(this.modalEl, modalHost);
		this.emitModalContentIfChanged();
	}

	public parkModal(parkingHost: HTMLElement): void {
		this.currentModalHost = parkingHost;
		this.attachElement(this.modalEl, parkingHost);
		this.emitModalContentIfChanged();
	}

	public startModalGuardian(): void {
		if (this.modalGuardian) {
			return;
		}

		const modal = this.modalEl;

		this.modalGuardian = new MutationObserver(() => {
			if (this.isReinsertingModal || modal.isConnected) {
				return;
			}

			this.isReinsertingModal = true;
			this.modalGuardian?.disconnect();

			try {
				const host = this.currentModalHost ?? document.body;
				host.appendChild(modal);

				debugWarn("[CaptainUpDOM] modal element was detached and has been restored", {
					hostId: host.id || null,
					hostAria: host.getAttribute?.("aria-label") ?? null,
					modal: snapshot(modal),
				});
			} finally {
				this.modalGuardian?.observe(document.body, { childList: true, subtree: true });
				this.isReinsertingModal = false;
				this.emitModalContentIfChanged();
			}
		});

		this.modalGuardian.observe(document.body, { childList: true, subtree: true });
	}

	public startModalContentObserver(): void {
		if (this.modalContentObserver) {
			return;
		}

		this.modalContentObserver = new MutationObserver(() => {
			this.emitModalContentIfChanged();
		});

		this.modalContentObserver.observe(this.modalEl, {
			childList: true,
			subtree: true,
			attributes: true,
			characterData: true,
		});

		this.emitModalContentIfChanged();
	}

	public startDockContentObserver(): void {
		if (this.dockContentObserver) {
			return;
		}

		debugLog("[CaptainUpDOM] starting dock content observer", {
			mission: snapshot(this.missionsEl),
			tournament: snapshot(this.tournamentEl),
			initialDockContentState: this.lastKnownDockContentState,
		});

		this.dockContentObserver = new MutationObserver(() => {
			this.emitDockContentIfChanged();
		});

		const observerOptions: MutationObserverInit = {
			childList: true,
			subtree: true,
			attributes: true,
			characterData: true,
		};

		this.dockContentObserver.observe(this.missionsEl, observerOptions);
		this.dockContentObserver.observe(this.tournamentEl, observerOptions);

		this.emitDockContentIfChanged();
	}

	public subscribeModalContent(handler: CaptainUpModalContentHandler): () => void {
		this.modalContentHandlers.add(handler);
		handler(this.lastKnownModalHasContent);

		return () => {
			this.modalContentHandlers.delete(handler);
		};
	}

	public subscribeDockContent(handler: CaptainUpDockContentHandler): () => void {
		this.dockContentHandlers.add(handler);
		handler(this.lastKnownDockContentState);

		return () => {
			this.dockContentHandlers.delete(handler);
		};
	}

	private emitModalContentIfChanged(): void {
		const nextHasContent = elementHasMeaningfulContent(this.modalEl);

		if (nextHasContent === this.lastKnownModalHasContent) {
			return;
		}

		this.lastKnownModalHasContent = nextHasContent;

		debugLog("[CaptainUpDOM] modal content changed", {
			hasContent: nextHasContent,
			modal: snapshot(this.modalEl),
		});

		for (const handler of this.modalContentHandlers) {
			try {
				handler(nextHasContent);
			} catch (error) {
				debugWarn("[CaptainUpDOM] modal content handler failed", error);
			}
		}
	}

	private readDockContentState(): CaptainUpDockContentState {
		return {
			missionHasContent: elementHasMeaningfulContent(this.missionsEl),
			tournamentHasContent: elementHasMeaningfulContent(this.tournamentEl),
		};
	}

	private emitDockContentIfChanged(): void {
		const nextState = this.readDockContentState();

		if (areDockContentStatesEqual(nextState, this.lastKnownDockContentState)) {
			return;
		}

		this.lastKnownDockContentState = nextState;

		debugLog("[CaptainUpDOM] dock content changed", {
			contentState: nextState,
			mission: snapshot(this.missionsEl),
			tournament: snapshot(this.tournamentEl),
		});

		for (const handler of this.dockContentHandlers) {
			try {
				handler(nextState);
			} catch (error) {
				debugWarn("[CaptainUpDOM] dock content handler failed", error);
			}
		}
	}

	private attachElement(el: HTMLElement, host: HTMLElement): void {
		if (el.parentElement === host) {
			return;
		}

		host.appendChild(el);

		debugLog("[CaptainUpDOM] element attached", {
			element: snapshot(el),
			hostId: host.id || null,
			hostAria: host.getAttribute?.("aria-label") ?? null,
		});

		if (el === this.missionsEl || el === this.tournamentEl) {
			this.emitDockContentIfChanged();
		}

		if (el === this.modalEl) {
			this.emitModalContentIfChanged();
		}
	}

	private ensureElement(id: string): HTMLElement {
		let el = document.getElementById(id) as HTMLElement | null;

		if (!el) {
			el = document.createElement("div");
			el.id = id;
			document.body.appendChild(el);

			debugLog("[CaptainUpDOM] created missing container element", {
				id,
			});
		}

		return el;
	}
}

const DEV_BRAND_ID = 1974;
const DEV_CLUSTER = "rubyplay4-dev";

function readDemoPlayerFromLocation(): DemoPlayer {
	const url = new URL(window.location.href);

	const id = url.searchParams.get("captainup_player_id");
	const name = url.searchParams.get("captainup_player_name");
	if (id && name) {
		return { playerId: id, playerName: name };
	}

	const raw = url.searchParams.get("playerSession") || "";
	const match = raw.match(/^player-(\d+)$/);
	if (match) {
		return { playerId: match[1], playerName: `player-${match[1]}` };
	}

	return { playerId: "1", playerName: "player-1" };
}

function resolveGameName(ctx?: CaptainUpBootContext): string | null {
	const url = new URL(window.location.href);

	const fromContext = (ctx?.gameName ?? "").trim();
	if (fromContext.length > 0) {
		return fromContext;
	}

	const fromGameName = (url.searchParams.get("gameName") ?? "").trim();
	if (fromGameName.length > 0) {
		return fromGameName;
	}

	const fromGamename = (url.searchParams.get("gamename") ?? "").trim();
	if (fromGamename.length > 0) {
		return fromGamename;
	}

	return null;
}

function resolveLang(ctx?: CaptainUpBootContext): string | null {
	const url = new URL(window.location.href);

	const fromContext = (ctx?.lang ?? "").trim();
	if (fromContext.length > 0) {
		return fromContext;
	}

	const fromUrl = (url.searchParams.get("lang") ?? "").trim();
	if (fromUrl.length > 0) {
		return fromUrl;
	}

	return null;
}

function ensureCaptainUpUrlParams(gameName: string | null, lang: string | null): void {
	const url = new URL(window.location.href);

	if (gameName) {
		url.searchParams.set("gameName", gameName);
		url.searchParams.set("gamename", gameName);
	}

	if (lang) {
		url.searchParams.set("lang", lang);
	}

	window.history.replaceState({}, "", url);
}

function buildMinimalGameSectionsConfig(gameName: string): unknown {
	return {
		[gameName]: {
			tournament: {
				infoContent: `
          <div style="color:#ffffff;font-family:Roboto,Arial,sans-serif;">
            <h1 style="text-align:center;margin:0 0 8px 0;">Tournament</h1>
            <p style="margin:0;">Demo content for ${gameName}.</p>
          </div>
        `,
			},
			missions: {
				infoContent: `
          <div style="color:#ffffff;font-family:Roboto,Arial,sans-serif;">
            <h1 style="text-align:center;margin:0 0 8px 0;">Missions</h1>
            <p style="margin:0;">Demo content for ${gameName}.</p>
          </div>
        `,
			},
		},
	};
}

function applyAuthOrThrow(authMode: CaptainUpAuthMode): void {
	const cpt = window._CaptainUpWidget;
	if (!cpt) {
		throw new Error("[CaptainUpBoot] window._CaptainUpWidget missing after cptLoadWidget()");
	}

	if (authMode.mode === "demo") {
		const player = readDemoPlayerFromLocation();
		cpt.setBrand?.(DEV_BRAND_ID, DEV_CLUSTER);

		if (typeof cpt.login === "function") {
			cpt.login(player.playerId, player.playerName);
		}

		return;
	}

	const auth = authMode.auth;

	if (typeof cpt.setSignedUser !== "function") {
		throw new Error("[CaptainUpBoot] cpt.setSignedUser missing (secure auth cannot proceed)");
	}

	const signedUser = String(auth?.signedUser ?? "").trim();
	const brandId = auth?.brandId;
	const cluster = String(auth?.cluster ?? "").trim();

	if (!signedUser) {
		throw new Error("[CaptainUpBoot] secure auth missing signedUser");
	}

	if (typeof brandId !== "number" || !Number.isFinite(brandId)) {
		throw new Error("[CaptainUpBoot] secure auth missing brandId");
	}

	if (!cluster) {
		throw new Error("[CaptainUpBoot] secure auth missing cluster");
	}

	cpt.setSignedUser(signedUser);
	cpt.setBrand?.(brandId, cluster);

	const userId = auth?.user?.id;
	if (
		typeof userId === "undefined" ||
		userId === null ||
		String(userId).trim().length === 0
	) {
		throw new Error("[CaptainUpBoot] secure auth missing user.id");
	}

	const name = typeof auth.user.name === "string" ? auth.user.name : undefined;
	const image = typeof auth.user.image === "string" ? auth.user.image : undefined;

	if (typeof cpt.login === "function") {
		cpt.login(userId, name, image);
	}
}

export async function bootCaptainUpOrThrow(
	authMode: CaptainUpAuthMode,
	theme?: CaptainUpTheme,
	modulesCsv?: string,
	ctx?: CaptainUpBootContext
): Promise<CaptainUpDomController> {
	debugLog("[CaptainUpBoot] boot starting", {
		authMode: summarizeAuthMode(authMode),
		gameName: ctx?.gameName ?? null,
		lang: ctx?.lang ?? null,
	});

	const dom = new CaptainUpDomController();

	await loadCaptainUpScript();

	if (typeof window.cptLoadWidget !== "function") {
		throw new Error("[CaptainUpBoot] window.cptLoadWidget not available after script load");
	}

	await window.cptLoadWidget();

	const cpt = window._CaptainUpWidget as CaptainUpWidgetApi;
	if (!cpt) {
		throw new Error("[CaptainUpBoot] window._CaptainUpWidget missing after cptLoadWidget()");
	}

	const resolvedGameName = resolveGameName(ctx);
	const resolvedLang = resolveLang(ctx) ?? "en";

	ensureCaptainUpUrlParams(resolvedGameName, resolvedLang);

	const resolvedTheme: CaptainUpTheme =
		theme ??
		ctx?.theme ?? {
			fonts: { headerFontName: "Montserrat", contentFontName: "Roboto" },
			layout: "popup9",
		};

	const resolvedModulesCsv = modulesCsv ?? ctx?.modulesCsv ?? "challenges, tournament";

	if (typeof cpt.setTheme === "function") {
		cpt.setTheme(resolvedTheme);
	}

	if (typeof cpt.enableModules === "function") {
		cpt.enableModules(resolvedModulesCsv);
	}

	if (resolvedGameName && typeof cpt.setGameSectionsConfig === "function") {
		cpt.setGameSectionsConfig(buildMinimalGameSectionsConfig(resolvedGameName));
	}

	applyAuthOrThrow(authMode);

	if (typeof cpt.renderer === "function") {
		cpt.renderer();
	} else {
		debugWarn("[CaptainUpBoot] renderer() is missing on widget api");
	}

	dom.startModalGuardian();
	dom.startModalContentObserver();
	dom.startDockContentObserver();

	if (typeof cpt.ui !== "function") {
		debugWarn("[CaptainUpBoot] widget api is missing ui(kind, action); modal control will not work");
	}

	debugLog("[CaptainUpBoot] boot complete", {
		mode: authMode.mode,
		gameName: resolvedGameName,
		lang: resolvedLang,
		widgetApiMethods: describeWidgetApi(cpt),
	});

	return dom;
}
